/*
 * Copyright (C) 2024 Reseau Concept.
 * https://reseau-concept.com
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */

/**
 * Extensions que Collabora sait EDITER.
 *
 * CETTE LISTE N'EST PAS UN CHOIX : elle recopie l'action `edit` de la page de decouverte que
 * Collabora publie sur `/hosting/discovery`. C'est la meme source que celle dont le module se sert
 * cote repository — `WopiDiscovery.getAction(ext)` — donc le seul moyen que les deux cotes soient
 * d'accord.
 *
 * La liste precedente etait fausse DANS LES DEUX SENS, ce qui se voyait a l'usage :
 *
 * - **des formats editables etaient absents**, l'action n'apparaissait donc jamais pour eux :
 *   csv, tsv, txt, dbf, dif, slk, xla, odg, odm, oth et les formats plats fodt / fods / fodp /
 *   fodg. Le csv et le txt sont les plus genants — le txt est meme ce que produit « Creer une
 *   note » ;
 *
 * - **des formats non editables etaient annonces**. Treize etaient inconnus de Collabora
 *   (ppam, pps, ppsm, sldm, sldx, vsdm, vssm, vssx, vst, vstm, vstx, xlam, xlt) : le bouton
 *   s'affichait et l'ouverture echouait en 501 « No action for node ». Onze autres n'existent
 *   qu'en lecture (dot, dotm, dotx, pot, potm, potx, vsd, vsdx, vss, xltm, xltx) ; pour ceux-la
 *   `getWopiSrcURL` retombe sur `actions.get(0)` et le document s'ouvrait EN LECTURE SEULE derriere
 *   un bouton intitule « Editer ».
 *
 * Pour ajouter une extension a un deploiement precis, sans toucher au code, passer par
 * `collabora.edit` dans la configuration de l'application : les deux listes sont concatenees.
 */
export const supportedEditExtensions = [
  'csv',
  'dbf',
  'dif',
  'doc',
  'docm',
  'docx',
  'fodg',
  'fodp',
  'fods',
  'fodt',
  'odg',
  'odm',
  'odp',
  'ods',
  'odt',
  'oth',
  'ppsx',
  'ppt',
  'pptm',
  'pptx',
  'rtf',
  'slk',
  'tsv',
  'txt',
  'xla',
  'xls',
  'xlsb',
  'xlsm',
  'xlsx'
];

/**
 * Extensions que Collabora sait AFFICHER, `view` et `view_comment` de la meme decouverte.
 *
 * Aucune action de consultation n'est declaree dans `collabora-online.plugin.json` — seule
 * `collabora.plugin.actions.edit` l'est — et le seul lecteur de cette liste, `getExtensions()`,
 * n'a lui-meme aucun appelant. Elle est donc conservee pour l'exactitude et pour les integrations
 * futures, sans effet aujourd'hui sur l'interface.
 */
export const supportedViewExtensions = [
  '123',
  '602',
  'abw',
  'bmp',
  'cdr',
  'cgm',
  'cwk',
  'dot',
  'dotm',
  'dotx',
  'dxf',
  'emf',
  'fb2',
  'fh',
  'gif',
  'gnumeric',
  'hwp',
  'jpeg',
  'jpg',
  'key',
  'lrf',
  'mw',
  'numbers',
  'otg',
  'otm',
  'otp',
  'ots',
  'ott',
  'p65',
  'pages',
  'pdb',
  'pdf',
  'png',
  'pot',
  'potm',
  'potx',
  'pub',
  'qpw',
  'stc',
  'std',
  'sti',
  'stw',
  'svg',
  'sxc',
  'sxd',
  'sxg',
  'sxi',
  'sxw',
  'tiff',
  'vsd',
  'vsdx',
  'vss',
  'wb1',
  'wk1',
  'wks',
  'wmf',
  'wpd',
  'wpg',
  'wps',
  'wq1',
  'wq2',
  'wri',
  'xlr',
  'xltm',
  'xltx'
];
export function canUseCollaboraExtension(context): boolean {
  if (context.navigation?.url?.startsWith('/trashcan')) {
    return false;
  }

  // check if collabora is enabled
  if (!context?.appConfig.get('collabora.enable')) {
    return false;
  }

  // check if element is selected
  if (!context?.selection) {
    return false;
  }

  return true;
}

export function getExtension(filename: string): string {
  const position = filename.lastIndexOf('.');
  return filename.substring(position + 1);
}

export function canEditWithCollaboraOnline(context): boolean {
  if (canUseCollaboraExtension(context)) {
    const { file } = context.selection;

    // check if is file
    if (!file?.entry) {
      return false;
    }

    // LES PROPRIETES SONT LUES APRES AVOIR ETE VERIFIEES, ET NON AVANT.
    //
    // L'ordre etait inverse : `properties['cm:lockLifetime']` etait lu en premier, et le
    // `if (!file.entry.properties)` qui protege cette lecture venait deux tests plus loin. Sur un
    // noeud sans propriete, la lecture levait donc une TypeError — et une exception dans un
    // evaluateur interrompt l'evaluation de TOUTE la barre d'outils et du menu contextuel, pas
    // seulement de cette entree. Le meme defaut avait ete corrige dans `canEditText` pour les
    // raccourcis, qui n'ont pas de propriete `content`.
    const properties = file.entry.properties;

    if (properties?.['cm:lockLifetime'] === 'EPHEMERAL') {
      return true;
    }

    // check if file is locked
    if (file.entry.isLocked) {
      return false;
    }
    if (!properties) {
      return false;
    }
    if (properties['cm:lockType'] === 'WRITE_LOCK' || properties['cm:lockType'] === 'READ_ONLY_LOCK') {
      return false;
    }
    const lockOwner = properties['cm:lockOwner'];
    if (lockOwner && lockOwner.id !== context.profile?.id) {
      return false;
    }

    // check the extension of file
    const extension: string = getExtension(file.entry.name).toLowerCase();
    if (!extension) {
      return false;
    }

    // `?? []` : sans la cle `collabora.edit` dans la configuration, `get` rend `undefined` et le
    // `.concat` qui suivait levait une TypeError — avec la meme consequence que ci-dessus.
    const configured: string[] = context.appConfig.get('collabora.edit') ?? [];
    const extCanEdit = configured.concat(supportedEditExtensions);
    if (extCanEdit.indexOf(extension) > -1) {
      return context.permissions.check(file, ['update']);
    }
    // Pas de trace ici : cette regle est reevaluee a chaque rafraichissement de la barre d'outils
    // et du menu contextuel. Le `console.log('Extension not allowed')` qui s'y trouvait ecrivait
    // donc en continu, pour tout fichier selectionne qui n'est pas editable.
  }
  return false;
}
